// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery } from '@apollo/client';
import loadable from '@loadable/component';
import { clientLogger } from '@seedplanner/logger';
import { sendToServer } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { MenuItem } from '../../lib/base';
import { PictureAsPdfIcon } from '../../lib/icons';
import * as routes from '../../routes';
import type {
  RecurringOrdersPreviewQuery,
  RecurringOrdersPreviewQueryVariables,
} from '../../types/__generated__/graphql';
import { Options } from '../common/dropdown';
import { sortOrderInputs } from '../order/sortOrderInputs';
import LoadingMenuItem from '../task/LoadingMenuItem';
import { makeRecurringOrdersPDF } from '../task/makeRecurringOrdersPDF';

const RECURRING_ORDERS_QUERY = gql`
  query RecurringOrdersPreview {
    recurring_orders(
      orderBy: [{ name: "recurring_order.customer", direction: "asc" }]
    ) {
      edges {
        node {
          id
          customer
          inputs {
            edges {
              node {
                id
                size_amount
                quantity
                unit
                crop {
                  id
                  crop_name
                }
                blend {
                  id
                  blend_name
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface Props {
  readonly useArrowOnly?: boolean;
}

// This prefetches when the component is mounted
const LoadableJSPDF = loadable.lib(() => import(/* webpackPrefetch: true */ 'jspdf'));

export default function RecurringOrderTableOptions({ useArrowOnly }: Props) {
  const [optionsClose, setOptionsClose] = React.useState(false);
  React.useEffect(() => {
    setOptionsClose(false);
  });

  const [getRecurringOrders] = useLazyQuery<RecurringOrdersPreviewQuery, RecurringOrdersPreviewQueryVariables>(
    RECURRING_ORDERS_QUERY,
  );

  const pdf = (
    <LoadableJSPDF key="export-to-pdf-loadable" fallback={<LoadingMenuItem />}>
      {({ jsPDF }) => (
        <MenuItem
          key="export-to-pdf-menu-item"
          onClick={() => {
            try {
              void getRecurringOrders({
                onCompleted: (data) => {
                  const orders = data.recurring_orders.edges.map(({ node }) => node);
                  makeRecurringOrdersPDF(jsPDF, {
                    orders: orders.map((order) => ({
                      customer: order.customer,
                      inputs: sortOrderInputs(order.inputs.edges.map(({ node }) => node)).map((input) => ({
                        sizeAmount: input.size_amount ?? undefined,
                        quantity: input.quantity,
                        unit: input.unit,
                        cropOrBlendName: input.crop?.crop_name ?? input.blend?.blend_name ?? '',
                      })),
                    })),
                    title: 'Recurring Orders Checklist',
                    downloadTitle: 'Recurring Orders Checklist',
                  });
                },
                onError: (error) => {
                  clientLogger.error('Error getting recurring orders!', { error });
                },
              });
            } catch (e) {
              clientLogger.error('Error generating PDF', { error: e });
              sendToServer({
                route: routes.ERROR,
                body: JSON.stringify({
                  errorMessage: 'Error generating PDF with JSPDF in task options',
                  location: window?.location?.toString() ?? 'window.location is undefined',
                }),
                title: 'no_jspdf_on_click_task_table',
              });
              enqueueSnackbar('Something went wrong generating the PDF! The team has been notified.', {
                variant: 'error',
                autoHideDuration: 10000,
              });
            }
            setOptionsClose(true);
          }}
          disableRipple
        >
          <PictureAsPdfIcon />
          Export to PDF
        </MenuItem>
      )}
    </LoadableJSPDF>
  );

  const options = [pdf];

  return <Options useArrowOnly={useArrowOnly} useMoreIcon={false} close={optionsClose} options={options} />;
}
